import React, { useEffect, useState } from "react";
import styles from "./HsCodesPopup.module.scss";
import { ReactComponent as UpArrow } from "../../icons/up-arrow.svg";
import { ReactComponent as DownArrow } from "../../icons/down-arrow.svg";
import OutsideClickHandler from "react-outside-click-handler";

const HsCodesPopup = ({
  open,
  root,
  expandedNodes,
  rootLoading,
  onClose,
  onExpand,
}) => {
  const [openNodes, setOpenNodes] = useState({});
  const [fetchedNodes, setFetchedNodes] = useState(new Set()); // Track which nodes have been fetched
  const [fetchingNodes, setFetchingNodes] = useState(new Set()); // Track which nodes are currently being fetched

  // Reset states whenever the popup is (re)opened
  useEffect(() => {
    if (open) {
      setOpenNodes({});
      setFetchedNodes(new Set());
      setFetchingNodes(new Set());
    }
  }, [open]);

  if (!open) return null;

  const handleToggle = async (code) => {
    const isCurrentlyOpen = !!openNodes[code];
    const children = expandedNodes[code] || [];
    const hasBeenFetched = fetchedNodes.has(code);
    const isFetching = fetchingNodes.has(code);

    // Prevent multiple fetches for the same node
    if (isFetching) return;

    // If not open and hasn't been fetched yet, fetch data
    if (!isCurrentlyOpen && !hasBeenFetched) {
      // Set fetching state
      setFetchingNodes((prev) => new Set(prev).add(code));

      try {
        await onExpand(code);
        setFetchedNodes((prev) => new Set(prev).add(code));
      } finally {
        // Remove from fetching state
        setFetchingNodes((prev) => {
          const newSet = new Set(prev);
          newSet.delete(code);
          return newSet;
        });
      }
    }

    // Toggle the node
    setOpenNodes((prev) => ({
      ...prev,
      [code]: !prev[code],
    }));
  };

  const renderCodes = (codes, level = 0, visited = new Set()) => {
    const sorted = [...codes].sort(
      (a, b) => Number(a.hscode) - Number(b.hscode),
    );

    return sorted.map((code) => {
      if (visited.has(code.hscode)) return null;
      const newVisited = new Set(visited).add(code.hscode);

      const isOpen = !!openNodes[code.hscode];
      const children = expandedNodes[code.hscode] || [];
      const hasBeenFetched = fetchedNodes.has(code.hscode);
      const isFetching = fetchingNodes.has(code.hscode);
      const hasNoChildren = hasBeenFetched && children.length === 0;

      // Determine the appropriate CSS class
      let codeLineClass = styles.codeLine;
      if (hasNoChildren) {
        codeLineClass += ` ${styles.noChildren}`;
      } else if (!hasBeenFetched || children.length > 0) {
        codeLineClass += ` ${styles.expandable}`;
      }

      return (
        <div key={code.hscode_full} className={styles.item}>
          <div
            className={codeLineClass}
            onClick={(e) => {
              e.stopPropagation();
              // Only allow toggle if there are children or it hasn't been fetched
              if (!hasNoChildren && !isFetching) {
                handleToggle(code.hscode);
              }
            }}
          >
            <div className={styles.codeContent}>
              {/* Expand/Collapse Indicator */}
              <span className={styles.indicator}>
                {isFetching ? (
                  <div className={styles.spinner}></div>
                ) : hasNoChildren ? (
                  <span className={styles.leafIcon}>•</span> // Bullet for leaf nodes
                ) : isOpen ? (
                  <UpArrow className={styles.arrowIcon} />
                ) : (
                  <DownArrow className={styles.arrowIcon} />
                )}
              </span>

              <div className={styles.textContent}>
                <div className={styles.topLine}>
                  <span className={styles.hscode}>{code.hscode_full}</span>

                  {/* Optional: Show status text */}
                  {hasNoChildren && (
                    <span className={styles.statusText}>(No subcodes)</span>
                  )}
                </div>

                <span className={styles.desc}>{code.description}</span>
              </div>
            </div>
          </div>

          {/* Render children only when open */}
          {isOpen && children.length > 0 && (
            <div className={styles.children}>
              {renderCodes(children, level + 1, newVisited)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div
      className={styles.overlay}
      data-ignore-outside-click="true"
      onClick={(e) => e.stopPropagation()}
    >
      <OutsideClickHandler onOutsideClick={onClose}>
        <div className={styles.popup}>
          <div className={styles.header}>
            <h3>HS Codes</h3>
            <button className={styles.closeBtn} onClick={onClose}>
              ✕
            </button>
          </div>

          <div className={styles.body}>
            {rootLoading ? (
              <div className={styles.noHsCodes}>Loading HS Codes...</div>
            ) : root.length > 0 ? (
              renderCodes(root)
            ) : (
              <div className={styles.noHsCodes}>No HS codes found.</div>
            )}
          </div>
        </div>
      </OutsideClickHandler>
    </div>
  );
};

export default HsCodesPopup;
